import { ChangeEvent, FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, FormControlLabel, Modal, Stack, TextField } from '@mui/material';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { ModalHeader } from '.';
import { modalActionBlockStyles, modalSmallContainerStyle } from './styles';

const MAX_LENGTH = 255;

export enum ModalTypeEnum {
  DELETE = 'delete',
  CREATE = 'create',
  CREATE_SUB_FOLDER = 'createSubFolder',
  RENAME = 'rename',
  RENAME_FILE = 'renameFile',
}

interface BasePayload {
  id: string;
}

export type CreateFolderPayload = BasePayload & {
  type: ModalTypeEnum.CREATE;
  onCreate: (data: { folder_path: string }) => void;
};

type CreateSubFolderPayload = BasePayload & {
  type: ModalTypeEnum.CREATE_SUB_FOLDER;
  onCreate: (data: { folder_path: string }) => void;
  parentFolderPath: string;
};

type RenameFolderPayload = BasePayload & {
  type: ModalTypeEnum.RENAME;
  onRename: (data: { current_path: string; new_path: string }) => void;
  folderPath: string;
};

export type RenameFilePayload = BasePayload & {
  type: ModalTypeEnum.RENAME_FILE;
  onRename: (data: { current_path: string; new_path: string }) => void;
  filePath: string;
};

type FileSystemActionPayload = CreateFolderPayload | CreateSubFolderPayload | RenameFolderPayload | RenameFilePayload;

export interface FileSystemActionModalProps {
  payload: FileSystemActionPayload;
}

const MODAL_CONFIG = {
  [ModalTypeEnum.CREATE]: {
    title: 'New Folder',
    label: 'FOLDER NAME',
    placeholder: 'Enter the Folder Name',
    submitLabel: 'Create',
  },
  [ModalTypeEnum.CREATE_SUB_FOLDER]: {
    title: 'New Folder',
    label: 'FOLDER NAME',
    placeholder: 'Enter the Folder Name',
    submitLabel: 'Create',
  },
  [ModalTypeEnum.RENAME]: {
    title: 'Rename',
    label: 'FOLDER NAME',
    placeholder: 'Enter the new name',
    submitLabel: 'Rename',
  },
  [ModalTypeEnum.RENAME_FILE]: {
    title: 'Rename',
    label: 'FILE NAME',
    placeholder: 'Enter the new name',
    submitLabel: 'Rename',
  },
} as const;

const FileSystemActionModal: FC<FileSystemActionModalProps> = (props) => {
  const dispatch = useAppDispatch();
  const { payload } = props;
  const { type } = payload;
  const [folderName, setFolderName] = useState('');
  const [fileExtension, setFileExtension] = useState('');

  const config = MODAL_CONFIG[type];
  const isFileType = type === ModalTypeEnum.RENAME_FILE;

  useEffect(() => {
    if (type === ModalTypeEnum.RENAME) {
      setFolderName(payload.folderPath.split('/').pop() || '');
      setFileExtension('');
    } else if (isFileType) {
      const currentName = payload.filePath.split('/').pop() || '';
      const lastDotIndex = currentName.lastIndexOf('.');
      if (lastDotIndex > 0) {
        setFolderName(currentName.substring(0, lastDotIndex));
        setFileExtension(currentName.substring(lastDotIndex));
      } else {
        setFolderName(currentName);
        setFileExtension('');
      }
    } else {
      setFolderName('');
      setFileExtension('');
    }
  }, [type, payload, isFileType]);

  const handleFolderNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const forbiddenChars = isFileType ? /[/\\<>:"|?*]/g : /[/.\\<>:"|?*]/g;
    setFolderName(e.target.value.replace(forbiddenChars, ''));
  };

  const handleStopPropagation = (e: MouseEvent) => e.stopPropagation();

  const isDisabled = useMemo(() => {
    const trimmedName = folderName.trim();
    if (!trimmedName || /[<>:"|?*]/.test(trimmedName)) return true;

    if (type === ModalTypeEnum.RENAME && 'folderPath' in payload) {
      return trimmedName === (payload.folderPath.split('/').pop() || '');
    }

    if (isFileType && 'filePath' in payload) {
      const currentName = payload.filePath.split('/').pop() || '';
      const lastDotIndex = currentName.lastIndexOf('.');
      const originalNameWithoutExt = lastDotIndex > 0 ? currentName.substring(0, lastDotIndex) : currentName;

      return trimmedName === originalNameWithoutExt;
    }

    return false;
  }, [folderName, type, payload, isFileType]);

  const handleClose = () => {
    dispatch(closeModalById(payload.id));
  };

  const handleSubmit = () => {
    const trimmedName = folderName.trim();

    if (type === ModalTypeEnum.CREATE) {
      payload.onCreate({ folder_path: trimmedName });
    } else if (type === ModalTypeEnum.CREATE_SUB_FOLDER) {
      payload.onCreate({ folder_path: `${payload.parentFolderPath}/${trimmedName}` });
    } else if (type === ModalTypeEnum.RENAME) {
      const parentPath = payload.folderPath.split('/').slice(0, -1).join('/');
      const newPath = parentPath ? `${parentPath}/${trimmedName}` : trimmedName;
      payload.onRename({ current_path: payload.folderPath, new_path: newPath });
    } else if (isFileType) {
      const parentPath = payload.filePath.split('/').slice(0, -1).join('/');
      const newFileName = trimmedName + fileExtension;
      const newPath = parentPath ? `${parentPath}/${newFileName}` : newFileName;
      payload.onRename({ current_path: payload.filePath, new_path: newPath });
    }
    handleClose();
  };

  return (
    <Modal open onClose={handleClose}>
      <Box sx={modalSmallContainerStyle}>
        <ModalHeader onClose={handleClose} title={[config.title]} />
        <Stack pb={5} px={3} spacing={2}>
          <FormControlLabel
            label={config.label}
            required
            control={
              <TextField
                slotProps={{ htmlInput: { maxLength: MAX_LENGTH } }}
                value={folderName}
                placeholder={config.placeholder}
                onClick={handleStopPropagation}
                onChange={handleFolderNameChange}
                autoFocus
                helperText={
                  isFileType && fileExtension ? `File extension "${fileExtension}" will be preserved` : undefined
                }
              />
            }
          />
        </Stack>
        <Box sx={modalActionBlockStyles}>
          <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} />
          <CustomButton label={config.submitLabel} disabled={isDisabled} onClick={handleSubmit} />
        </Box>
      </Box>
    </Modal>
  );
};

export default FileSystemActionModal;
